import React, { useState, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Tooltip } from 'primereact/tooltip';

const EVIDENCE_STYLES = {
    consensus: { color: '#3b82f6', label: 'Consensus' },   // blu
    curated:   { color: '#22c55e', label: 'Curated' },     // verde
    derived:   { color: '#8b5e3c', label: 'Derived' },     // marrone
    homology:  { color: '#64748b', label: 'Homology' },    // grigio/slate
    predicted: { color: '#a855f7', label: 'Predicted' },   // viola
};

const ECO_TO_TYPE = {
    // Curated (experimental / manual curation)
    'ECO:0000269': 'curated',  // experimental evidence
    'ECO:0000305': 'curated',  // curator inference
    'ECO:0000244': 'curated',  // combinatorial evidence (manual)
    'ECO:0000314': 'curated',  // direct assay
    'ECO:0000353': 'curated',  // physical interaction

    // Homology (by similarity / sequence model)
    'ECO:0000250': 'homology', // by similarity
    'ECO:0000255': 'homology', // sequence model evidence (manual)
    'ECO:0000257': 'homology', // match to sequence model evidence

    // Derived (author statements / imported)
    'ECO:0000303': 'derived',  // non-traceable author statement
    'ECO:0000304': 'derived',  // traceable author statement
    'ECO:0000312': 'derived',  // imported information
    'ECO:0007829': 'derived',

    // Predicted (automatic assertion)
    'ECO:0000256': 'predicted', // automatic assertion
    'ECO:0000213': 'predicted', // combinatorial (automatic)
    'ECO:0007744': 'predicted', // combinatorial computational + experimental
    'ECO:0000501': 'predicted', // evidence used in automatic assertion
};

const classifyEco = (ecoCode) => ECO_TO_TYPE[ecoCode] || null;

const evidenceBodyTemplate = (field) => (rowData) => {
    const raw = rowData[field];
    if (!raw) return null;

    const text = String(raw);
    const parts = text.split(/(ECO:\d{7})/g);

    return (
        <span style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {parts.map((part, i) => {
                const type = classifyEco(part);
                if (type) {
                    return (
                        <span
                            key={i}
                            style={{
                                color: EVIDENCE_STYLES[type].color,
                                fontWeight: 600,
                            }}
                            title={EVIDENCE_STYLES[type].label}
                        >
                            {part}
                        </span>
                    );
                }
                return <span key={i}>{part}</span>;
            })}
        </span>
    );
};

const EvidenceLegend = () => {
    const items = ['curated', 'derived', 'homology', 'predicted'];
    return (
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            {items.map((type) => (
                <span key={type} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <span
                        style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: EVIDENCE_STYLES[type].color,
                        }}
                    />
                    {EVIDENCE_STYLES[type].label}
                </span>
            ))}
        </div>
    );
};

const ResiduesTable = ({ ResidueData }) => {
    const dt = useRef(null);

    const handleClick = () => {
        navigator.clipboard.writeText(window.location.href);
    };

    const columns = [
        { field: 'anPosition', header: 'Position' },
        { field: 'unRefProtRe', header: 'Ref' },
        { field: 'afRes', header: 'afRef' },
        { field: 'unAnnNatVariants', header: 'Natural variants' },
        { field: 'unMoleculeProcessing', header: 'Molecule processing' },
        { field: 'unLocation', header: 'Location' },
        { field: 'unMotives', header: 'Motives' },
        { field: 'unDomains', header: 'Domains' },
        { field: 'pfamDomainPosHmm', header: 'PFAM-HMM Pos' },
        { field: 'unLigandsBindingSite', header: 'Binding site' },
        { field: 'unAnnPropActiveSite', header: 'Active site' },
        { field: 'phosPtms', header: 'PTMs' },
        { field: 'unGlycosylations', header: 'Glycosylations' },
        { field: 'unLipidations', header: 'Lipidations' },
        { field: 'unModifiedRes', header: 'Modified Residue' },
        { field: 'unDisulfideBongBridges', header: 'Disulfide bond bridges' },
        { field: 'unSecondaryStructure', header: 'Secondary Structure' },
        { field: 'afPredictedSecondaryStructure', header: 'AF-Predicted secondary structure' },
        { field: 'afConfidenceValue', header: 'AF-Confidence Value (pLDDT)' },
        { field: 'unCrossLinks', header: 'CrossLinks' },
        { field: 'inBindingRegion', header: 'Binding Region' },
        { field: 'interproDomain', header: 'InterPro' },
        { field: 'aamissense.amMean', header: 'amMean' },
        { field: 'aamissense.amClass', header: 'amClass' },
        { field: 'aamissense.amMax', header: 'amMax' },
        { field: 'aamissense.amMin', header: 'amMin' },
        { field: 'aamissense.amPathogenicFrac', header: 'amPathogenicFrac' },

       // { field: 'esm.esmMean', header: 'esmMean' },
       // { field: 'esm.esmClass', header: 'esmClass' },
       // { field: 'esm.esmMax', header: 'esmMax' },
       // { field: 'esm.esmMin', header: 'esmMin' },
       // { field: 'esm.esmConservationFrac', header: 'esmConservationFrac' },

       // { field: 'thermompnn.ddgMean', header: 'ddgMean' },
       // { field: 'thermompnn.ddgClass', header: 'ddgClass' },
       // { field: 'thermompnn.ddgMax', header: 'ddgMax' },
       // { field: 'thermompnn.ddgMin', header: 'ddgMin' },
       // { field: 'thermompnn.fracDestabilizing', header: 'fracDestabilizing' },

       //  { field: 'residualScore.rMedianAbsNegative', header: 'rMedianAbsNegative' },

        // --- Evidence fields (hidden by default, available via MultiSelect) ---
        { field: 'unAnnNatVariantsEvidence', header: 'Natural variants (evidence)' },
        { field: 'unMoleculeProcessingEvidence', header: 'Molecule processing (evidence)' },
        { field: 'unLocationEvidence', header: 'Location (evidence)' },
        { field: 'unMotivesEvidence', header: 'Motives (evidence)' },
        { field: 'unDomainsEvidence', header: 'Domains (evidence)' },
        { field: 'unLigandsBindingSiteEvidence', header: 'Binding site (evidence)' },
        { field: 'unAnnPropActiveSiteEvidence', header: 'Active site (evidence)' },
        { field: 'unGlycosylationsEvidence', header: 'Glycosylations (evidence)' },
        { field: 'unLipidationsEvidence', header: 'Lipidations (evidence)' },
        { field: 'unModifiedResEvidence', header: 'Modified Residue (evidence)' },
        { field: 'unDisulfideBongBridgesEvidence', header: 'Disulfide bond bridges (evidence)' },
        { field: 'unSecondaryStructureEvidence', header: 'Secondary Structure (evidence)' },
        { field: 'unCrossLinksEvidence', header: 'CrossLinks (evidence)' },
    ];

    const evidenceFields = new Set(columns
        .filter(c => c.field.endsWith('Evidence'))
        .map(c => c.field));

    const multiSelectOptions = columns.map(col => ({ label: col.header, value: col.field }));

    const [visibleColumns, setVisibleColumns] = useState(
        columns.filter(col => !evidenceFields.has(col.field))
    );

    const onColumnToggle = (event) => {
        const selectedFieldNames = event.value;
        const updatedVisibleColumns = columns.filter(col => selectedFieldNames.includes(col.field));
        setVisibleColumns(updatedVisibleColumns);
    };

    const renderNumericFilter = (options) => {
        return (
            <InputText
                type="number"
                value={options.value || ''}
                onChange={(e) => options.filterCallback(e.target.value, options.index)}
                placeholder="Enter number"
                className="p-column-filter"
            />
        );
    };

    const dynamicColumns = visibleColumns.map((col) => {
        const isNumeric = col.field === 'afConfidenceValue';
        const isEvidence = evidenceFields.has(col.field);

        return (
            <Column
                key={col.field}
                className="nobreak"
                field={col.field}
                header={col.header}
                sortable
                filter
                filterField={col.field}
                dataType={isNumeric ? 'numeric' : 'text'}
                filterElement={isNumeric ? renderNumericFilter : undefined}
                filterPlaceholder={`Search by ${col.header}`}
                body={isEvidence ? evidenceBodyTemplate(col.field) : undefined}
            />
        );
    });

    const exportCSV = (tableRef, selectionOnly) => {
        tableRef.current.exportCSV({ selectionOnly });
    };

    const exportExcel = async (dataToExport) => {
        const xlsx = await import('xlsx');
        const worksheet = xlsx.utils.json_to_sheet(dataToExport);
        const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAsExcelFile(excelBuffer, 'data');
    };

    const saveAsExcelFile = (buffer, fileName) => {
        import('file-saver').then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                let EXCEL_EXTENSION = '.xlsx';
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });

                module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        anPosition: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        unRefProtRe: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        afRes: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unAnnNatVariants: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unMoleculeProcessing: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unLocation: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unMotives: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unDomains: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        pfamDomainPosHmm: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unLigandsBindingSite: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unAnnPropActiveSite: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        phosPtms: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unGlycosylations: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unLipidations: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unModifiedRes: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unDisulfideBongBridges: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        unSecondaryStructure: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        afPredictedSecondaryStructure: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        afConfidenceValue: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
        unCrossLinks: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        inBindingRegion: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        interproDomain: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    });

    const value = filters['global'] ? filters['global'].value : '';

    const onGlobalFilterChange = (event) => {
        const value = event.target.value.replace(/\s+/g, "");
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
    };

    const clearFilter = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            anPosition: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            unRefProtRe: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            afRes: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unAnnNatVariants: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unMoleculeProcessing: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unLocation: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unMotives: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unDomains: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            pfamDomainPosHmm: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unLigandsBindingSite: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unAnnPropActiveSite: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            phosPtms: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unGlycosylations: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unLipidations: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unModifiedRes: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unDisulfideBongBridges: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            unSecondaryStructure: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            afPredictedSecondaryStructure: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            afConfidenceValue: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            unCrossLinks: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            inBindingRegion: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            interproDomain: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        });
    };

    const tagsBodyTemplate = (rowData) => {
        if (!rowData.summary || !rowData.anPosition) return null;
        const annotations = rowData.summary;

        if (!annotations) return null;

        return (
            <div className="flex gap-1 justify-content-center">
                {annotations.has_clinvar && (
                    <>
                        <Tooltip target=".has_clinvar" />
                        <i className="has_clinvar pi pi-shield p-1" style={{ color: '#708090' }} data-pr-tooltip="Has ClinVar variant"
                            data-pr-position="right"
                            data-pr-at="right+5 top"
                            data-pr-my="left center-2"></i>
                    </>
                )}
                {annotations.has_intra_contact && (
                    <>
                        <Tooltip target=".has_intra_contact" />
                        <i className="has_intra_contact pi pi-globe p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Intra-protein Contact" data-pr-position="right"
                            data-pr-at="right+5 top"
                            data-pr-my="left center-2"></i>
                    </>
                )}
                {annotations.has_af_intra_contact && (
                    <>
                        <Tooltip target=".has_af_intra_contact" />
                        <i className="has_af_intra_contact pi pi pi-star p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Intra-protein Contact" data-pr-position="right"
                            data-pr-at="right+5 top"
                            data-pr-my="left center-2"></i>
                    </>
                )}
                {annotations.has_inter_contact && (
                    <>
                        <Tooltip target=".has_inter_contact" />
                        <i className="has_inter_contact pi pi-sitemap p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Inter-protein Interaction"
                            data-pr-position="right"
                            data-pr-at="right+5 top"
                            data-pr-my="left center-2"></i>
                    </>
                )}
                {annotations.has_af_inter_contact && (
                    <>
                        <Tooltip target=".has_af_inter_contact" />
                        <i className="has_af_inter_contact pi pi-star-fill p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Inter-protein Contact" data-pr-position="right"
                            data-pr-at="right+5 top"
                            data-pr-my="left center-2"></i>
                    </>
                )}
                {annotations.has_ligand && (
                    <>
                        <Tooltip target=".has_ligand" />
                        <i className="has_ligand pi pi-ethereum p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Ligand Contact"
                            data-pr-position="right"
                            data-pr-at="right+5 top"
                            data-pr-my="left center-2"></i>
                    </>
                )}
            </div>
        );
    };

    const header = (
        <div className="row align-items-center">
            <div className="col mb-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <IconField iconPosition="left" className="me-1">
                    <InputIcon className="pi pi-search"> </InputIcon>
                    <InputText type="search" value={value || ''} onChange={onGlobalFilterChange}
                        placeholder="Global Search" />
                </IconField>
                <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} />
            </div>
            <div className="col" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <MultiSelect
                    value={visibleColumns.map(col => col.field)}
                    options={multiSelectOptions}
                    onChange={onColumnToggle}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select Columns"
                    display="chip"
                    style={{ width: '200px', marginRight: '10px' }}
                />
                <Button type="button" icon="pi pi-file" className="p-button-rounded p-mr-2" onClick={() => exportCSV(dt, false)} data-pr-tooltip="CSV" />
                <Button type="button" icon="pi pi-file-excel" className="p-button-success p-button-rounded p-mr-2" onClick={() => exportExcel(ResidueData)} data-pr-tooltip="XLS" />
                <Button type="button" icon="pi pi-link" severity="help" className="p-button-rounded p-mr-2" onClick={handleClick} />
            </div>
        </div>
    );

    return (
        <>
            <DataTable
                ref={dt}
                value={ResidueData}
                paginator
                rows={5}
                removableSort
                header={header}
                filters={filters}
                onFilter={(e) => setFilters(e.filters)}
                filterDisplay="menu"
                className="nobreak"
                tableStyle={{ minWidth: '60rem' }}
            >
                <Column
                    header="Tags"
                    body={tagsBodyTemplate}
                    style={{ width: '5rem' }}
                    align="center"
                />
                {dynamicColumns}
            </DataTable>

            <EvidenceLegend />
            <p style={{ textAlign: 'center', fontSize: 12, color: '#666', marginTop: 6 }}>
                Evidence columns are hidden by default — enable them from the column selector above.
            </p>
        </>
    );
};

export default ResiduesTable;